/* The details HR and Accounts would have supplied, typed in by hand, and the button that creates the person
 * in SignIn, the Training Matrix and EZ Leave with them.
 *
 * Two homes, one form: the Review step of Add a joiner when IT finishes it themselves, and the person drawer
 * for somebody who has already been stamped as set up but was never created in those systems (added by hand,
 * or bypassed before this existed). The rules and the wording are in lib/systems.ts, where they are tested;
 * the Function does the writing and is idempotent, so pressing it twice is safe.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  WEEKDAYS,
  defaultPayroll,
  joinerFromPerson,
  payrollProblems,
  type IPayrollInput,
  type ISystemsOutcome,
} from '../../lib/systems';
import type { IPerson } from '../../models';
import { useServices } from '../../services/context';
import { Button } from '../ui';
import s from './screens.module.scss';

/** The fields alone: controlled, so the wizard can hold the values and validate them with the rest of Review. */
export function SystemsFields({
  value,
  onChange,
  disabled,
}: {
  value: IPayrollInput;
  onChange: (next: IPayrollInput) => void;
  disabled?: boolean;
}): JSX.Element {
  const set = (patch: Partial<IPayrollInput>): void => onChange({ ...value, ...patch });
  const toggleDay = (d: string): void =>
    set({ workingPattern: value.workingPattern.includes(d) ? value.workingPattern.filter((x) => x !== d) : [...value.workingPattern, d] });
  const field: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-sm)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={field}>
        <b>Days they work</b>
        <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {WEEKDAYS.map((d) => (
            <label key={d} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="checkbox" disabled={disabled} checked={value.workingPattern.includes(d)} onChange={() => toggleDay(d)} />
              {d}
            </label>
          ))}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label style={field}>
          <b>Holiday entitlement (days a year)</b>
          <input
            className={s.stepSelect}
            type="text"
            inputMode="decimal"
            disabled={disabled}
            value={value.entitlement}
            onChange={(e) => set({ entitlement: e.target.value })}
            placeholder="e.g. 28"
            aria-label="Holiday entitlement in days"
          />
        </label>
        <label style={field}>
          <b>Employee number (optional)</b>
          <input
            className={s.stepSelect}
            type="text"
            disabled={disabled}
            value={value.employeeNumber}
            onChange={(e) => set({ employeeNumber: e.target.value })}
            aria-label="Employee number"
          />
        </label>
      </div>
      <span style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" disabled={disabled} checked={value.companySickPay} onChange={(e) => set({ companySickPay: e.target.checked })} />
          Company sick pay
        </label>
        <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" disabled={disabled} checked={value.rolledUpHolidayPay} onChange={(e) => set({ rolledUpHolidayPay: e.target.checked })} />
          Rolled-up holiday pay
        </label>
      </span>
    </div>
  );
}

/** For the person drawer: create them in the other systems now, for somebody who was set up some other way. */
export function SystemsPanel({ person, depotName }: { person: IPerson; depotName: string }): JSX.Element {
  const svc = useServices();
  const [open, setOpen] = useState(false);
  const [payroll, setPayroll] = useState<IPayrollInput>(defaultPayroll());
  const problems = payrollProblems(payroll);
  const missing = [
    !person.graphId ? 'The sync has not matched this account to Entra yet.' : '',
    !depotName ? 'They have no depot.' : '',
  ].filter(Boolean);

  const run = useMutation<ISystemsOutcome>({
    meta: { quiet: true },
    mutationFn: () => svc.onboarding.setUpSystems({ joiner: joinerFromPerson(person, depotName), payroll }),
  });

  return (
    <div className={s.stepRow}>
      <b>Create them in SignIn, the Training Matrix and EZ Leave</b>
      <div className={s.stepDetail}>
        The records HR and Accounts normally create when they finish a case. Anything they are already in is
        left alone, so this is safe to press if you are not sure.
      </div>

      {!open ? (
        <Button onClick={() => setOpen(true)}>Enter their details…</Button>
      ) : (
        <>
          <SystemsFields value={payroll} onChange={setPayroll} disabled={run.isPending} />
          <div className={s.ownedNote}>
            National insurance, tax code and bank details are not asked for here; they stay with Accounts.
          </div>
          {[...missing, ...problems].length > 0 ? <div className={s.stepDetail}>{[...missing, ...problems].join(' ')}</div> : null}
          <div>
            <Button primary disabled={run.isPending || problems.length > 0 || missing.length > 0} onClick={() => run.mutate()}>
              {run.isPending ? 'Creating…' : 'Create their records'}
            </Button>{' '}
            <Button disabled={run.isPending} onClick={() => setOpen(false)}>Close</Button>
          </div>
          {run.data ? (
            <div className={run.data.ok ? s.stepDetail : s.stepError}>
              {run.data.ok ? 'Done. ' : 'Not all of it worked. '}
              {run.data.message}
              {!run.data.ok && Object.keys(run.data.steps).length > 0 ? ' Pressing the button again retries only what is missing.' : ''}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
